import { useEffect, useState } from "react";
import { Button } from "../../../../components/New/Button";
import { PageHeader } from "../../../../components/Headers/PageHeader";
import { SelectInput } from "../../../../components/SelectInput";
import { useAccountingSalesReport } from "../../../../hooks/useAccountingSalesReport";
import { useTranslation } from "react-i18next";
import * as S from "./styles";
import { useTranslateMonths } from "../../../../hooks/useTranslateMonths";

export function SalesTaxReport() {
  const { t } = useTranslation();

  const { translatedMonthsOptions } = useTranslateMonths();

  const currentYear = new Date().getFullYear();

  const [reportFilter, setReportFilter] = useState({
    month: "1",
    year: currentYear.toString(),
  });

  const [yearsOptions, setYearsOptions] = useState<
    {
      id: string;
      label: string;
      value: string;
    }[]
  >([]);

  const filteredMonths =
    currentYear.toString() === reportFilter.year
      ? getPastAndCurrentMonths()
      : translatedMonthsOptions;

  const { getAccountingSalesReport, useAccountingSalesReportLoading } =
    useAccountingSalesReport();

  function handleGetAccountingSalesReport() {
    const month = Number(reportFilter.month);
    const year = Number(reportFilter.year);

    const formattedMonth = month < 10 ? `0${month}` : month;

    const startDate = `${year}-${formattedMonth}-01`;

    const nextMonth = month === 12 ? 1 : month + 1;
    const nextYear = month === 12 ? year + 1 : year;
    const formattedNextMonth = nextMonth < 10 ? `0${nextMonth}` : nextMonth;

    const endDate = `${nextYear}-${formattedNextMonth}-01`;

    getAccountingSalesReport({ startDate, endDate });
  }

  function getYears() {
    for (let year = currentYear; year >= 2023; year--) {
      setYearsOptions((currentValue) => [
        ...currentValue,
        {
          id: year.toString(),
          label: year.toString(),
          value: year.toString(),
        },
      ]);
    }
  }

  function getPastAndCurrentMonths() {
    const currentMonth = new Date().getMonth() + 1;

    return translatedMonthsOptions.filter(
      (month) => Number(month.value) <= currentMonth,
    );
  }

  function checkIfYearHasAvailableMonth() {
    const yearHasMonthSelected = filteredMonths.some(
      (month) => month.value === reportFilter.month,
    );

    if (!yearHasMonthSelected) {
      setReportFilter((currentState) => ({
        ...currentState,
        month: "1",
      }));
    }
  }

  useEffect(() => {
    checkIfYearHasAvailableMonth();
  }, [reportFilter.year]);

  useEffect(() => {
    getYears();
  }, []);

  return (
    <S.Container>
      <S.HeaderContainer>
        <PageHeader
          title={t("salesTaxReport")}
          description={t("downloadSalesTaxReport")}
          refreshFunction={() => {}}
          downloadFunction={handleGetAccountingSalesReport}
          loading={{
            type: "csv",
            isLoading: useAccountingSalesReportLoading,
          }}
          customBreadcrumbString={t("salesTaxReport")}
        />
      </S.HeaderContainer>

      <S.Content>
        <S.SelectInputWrapper>
          <SelectInput
            options={filteredMonths}
            variant="resized"
            onChange={(e) =>
              setReportFilter((currentState) => ({
                ...currentState,
                month: e.target.value,
              }))
            }
            value={reportFilter.month}
            title={t("selectMonth")}
            hasAsterisk
          />
        </S.SelectInputWrapper>

        <S.SelectInputWrapper>
          {yearsOptions[0] && (
            <SelectInput
              options={yearsOptions}
              variant="resized"
              onChange={(e) => {
                setReportFilter((currentState) => ({
                  ...currentState,
                  year: e.target.value,
                }));
              }}
              value={reportFilter.year}
              title={t("selectYear")}
              hasAsterisk
            />
          )}
        </S.SelectInputWrapper>

        <Button
          title={t("download")}
          disabled={useAccountingSalesReportLoading}
          isLoading={useAccountingSalesReportLoading}
          onClick={
            !useAccountingSalesReportLoading
              ? handleGetAccountingSalesReport
              : undefined
          }
          style={{
            cursor: useAccountingSalesReportLoading ? "not-allowed" : "pointer",
            opacity: useAccountingSalesReportLoading ? 0.7 : 1,
          }}
        />
      </S.Content>
    </S.Container>
  );
}
